import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, IconButton } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import UserStreaksData from './userStreaksData';
import UserCourseData from './userCourseData';
import { useProfileData } from '../../hooks/useProfileData';

const UserProfileSections = () => {
  const navigate = useNavigate();
  const { authUser: currentAuthUser } = useAuth();
  const { creatorProfileData: profileData, isLoadingProfile} = useProfileData();
  const [message, setMessage] = useState('');

  const {
    userId = '',
    userName = '',
    shortBio = '',
    introMessage,
  } = profileData || {};

  const isProfileAuthUser = currentAuthUser?.documentId === userId;

  const handleSendMessage = (inputStr) => {
    const messageToSend = inputStr || message;
    if (messageToSend?.trim()) {
      navigate(`/${userName}/chat`, {
        state: { message: messageToSend },
      });
    }
  };

  return (
    <Box width="100%">
      {/* Short Bio Section */}
      {shortBio && (
        <Box sx={{ paddingTop: '24px' }}>
          <Typography
            component="p"
            variant="body2"
            color="text.secondary"
          >
            Biography
          </Typography>
          <Typography
            component="p"
            variant="body2"
            color="text.primary"
            sx={{ paddingTop: '16px' }}
          >
            {shortBio}
          </Typography>
        </Box>
      )}

      {/* User Streak Data Section */}
      {
        !isLoadingProfile &&
          <UserStreaksData />
      }

      {/* Suggested Questions Section */}
      {typeof introMessage !== 'string' && introMessage?.chips ? (
        <Box
          width="100%"
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100dvw',
          }}
          gap={1.5}
        >
          <Typography
            component="p"
            variant="body2"
            color="text.secondary"
            sx={{
              pb: 0.5,
              overflow: 'hidden',
            }}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            Suggested Questions
          </Typography>
          {introMessage.chips.map((val, ind) => (
            <Box
              component="a"
              key={`suggested-question-${ind}`}
              sx={{
                width: '100%',
                px: 3,
                py: 1.5,
                border: 1,
                borderRadius: (theme) => theme.shape.borderRadiusLg,
                borderColor: (theme) => theme.palette.divider,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
              href={`/${userName}/chat`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSendMessage(val);
              }}
            >
              <Typography variant="body2" color="primary">
                {val}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        !!isProfileAuthUser && (
          <Box
            width="100%"
            sx={{
              mt: 3,
              maxWidth: '100dvw',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              sx={{ mb: 1 }}
            >
              Suggested Questions
            </Typography>
            <Box
              sx={{
                textAlign: 'center',
                p: 3,
                backgroundColor: 'background.default',
                borderRadius: 2,
                border: '1px dashed rgba(0, 0, 0, 0.2)',
              }}
            >
              <Typography variant="h7" fontWeight={600} sx={{ mb: 1 }}>
                Create a suggested question for people who visit your profile.
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Visitors can click the suggested question to immediately start
                interacting with your AI persona.
              </Typography>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'grey.800',
                  color: 'white',
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'grey.900',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/${currentAuthUser?.userName}/update/introduction`);
                }}
              >
                Create Suggested Question
              </Button>
            </Box>
          </Box>
        )
      )}

      {/* Course Data Section */}
      <Box width="100%" paddingTop="24px">
        <UserCourseData
          authUser={currentAuthUser}
          profileData={profileData}
        />
      </Box>

    </Box>
  );
};

// You'll need to define this icon or import it from your library
const SendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
  </svg>
);

export default UserProfileSections;
